<template>
  <div class="container pull-up">
    <v-row class="d-flex justify-center" align="center">
      <v-col cols="12" sm="12" md="10">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-store-plus</v-icon>
            {{ $t('store.title.create') }}
          </v-card-title>
          <v-card-text>
            <v-form ref="form">
              <!-- Basic Information -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="data.name"
                    :label="$t('store.label.name')"
                    filled
                    :rules="rules.nameRules"
                    prepend-inner-icon="mdi-store"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="data.phone"
                    :label="$t('store.label.phone')"
                    filled
                    prepend-inner-icon="mdi-phone"
                  />
                </v-col>
              </v-row>

              <v-textarea
                rows="2"
                v-model="data.description"
                :label="$t('store.label.description')"
                filled
                prepend-inner-icon="mdi-text"
              />

              <v-text-field
                v-model="data.address"
                :label="$t('store.label.address')"
                filled
                prepend-inner-icon="mdi-map-marker"
                :rules="rules.addressRules"
              />

              <!-- Address Information -->
              <v-divider class="my-4"></v-divider>
              <h3 class="mb-3">
                <v-icon left>mdi-map</v-icon>
                Location Details
              </h3>

              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="data.country_id"
                    :items="countries"
                    item-text="name"
                    item-value="id"
                    label="Country"
                    filled
                    prepend-inner-icon="mdi-flag"
                    @change="onCountryChange"
                    :rules="rules.countryRules"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.code }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="data.country"
                    :label="$t('store.label.country')"
                    filled
                    prepend-inner-icon="mdi-earth"
                  />
                </v-col>
              </v-row>

              <!-- Map and Coordinates -->
              <v-row>
                <v-col cols="12">
                  <h4 class="mb-2">
                    <v-icon left>mdi-crosshairs-gps</v-icon>
                    Store Location
                  </h4>
                  <p class="text-caption mb-3">
                    Click on the map to select the store location, or use your current location
                  </p>

                  <LocationPicker
                    v-model="locationData"
                    :initial-center="mapCenter"
                    :zoom="mapZoom"
                    @change="onLocationChange"
                    @error="onMapError"
                    ref="locationPicker"
                  />
                </v-col>
              </v-row>

              <!-- Manual Coordinate Entry -->
              <v-row class="mt-3">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="data.latitude"
                    label="Latitude"
                    type="number"
                    step="0.000001"
                    filled
                    prepend-inner-icon="mdi-latitude"
                    :rules="rules.latitudeRules"
                    @input="onCoordinateChange"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="data.longitude"
                    label="Longitude"
                    type="number"
                    step="0.000001"
                    filled
                    prepend-inner-icon="mdi-longitude"
                    :rules="rules.longitudeRules"
                    @input="onCoordinateChange"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-divider class="mx-2 mb-0"/>
          <v-card-actions>
            <v-row class="d-flex justify-space-between" align="center">
              <v-col cols="auto">
                <v-btn color="warning" depressed :to="{name: 'stores'}">
                  <v-icon left>mdi-arrow-left-circle</v-icon>
                  Cancel
                </v-btn>
              </v-col>
              <v-col cols="auto">
                <v-btn color="primary" @click.prevent="createStore" :loading="loading">
                  {{ $t('store.button.create') }}
                  <v-icon right>mdi-check-circle</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import LocationPicker from '@/components/_Partials/LocationPicker.vue'

export default {
  name: 'create-store',
  components: {
    LocationPicker
  },
  data () {
    return {
      loading: false,
      countries: [],
      locationData: { latitude: null, longitude: null },
      mapCenter: { lat: 4.0511, lng: 9.7679 }, // Default to Cameroon
      mapZoom: 10,
      data: {
        name: '',
        description: '',
        address: '',
        phone: '',
        latitude: null,
        longitude: null,
        country: '',
        country_id: null
      },
      rules: {
        nameRules: [
          v => !!v || this.$t('store.message.required')
        ],
        addressRules: [
          v => !!v || 'Address is required'
        ],
        countryRules: [
          v => !!v || 'Country selection is required'
        ],
        latitudeRules: [
          v => v === null || v === '' || (v >= -90 && v <= 90) || 'Latitude must be between -90 and 90'
        ],
        longitudeRules: [
          v => v === null || v === '' || (v >= -180 && v <= 180) || 'Longitude must be between -180 and 180'
        ]
      }
    }
  },
  computed: {
  },
  methods: {
    async createStore () {
      if (this.$refs.form.validate()) {
        try {
          await this.withLoading(async () => {
            // Prepare data with address information
            const storeData = {
              ...this.data,
              latitude: this.data.latitude || this.locationData.latitude,
              longitude: this.data.longitude || this.locationData.longitude
            }

            const data = await this.$departmentService.createDepartment(storeData)
            this.displayMessage(this.$t('store.message.created'), () => {
              this.$router.push({
                name: 'stores',
                query: { store: data.id }
              })
            })
          }, 'loading')
        } catch (error) {
          // Error is already handled by the global interceptor
          console.log('Component caught error in createDepartment:', error)
        }
      }
    },

    resetForm () {
      this.$refs.form.resetFields()
      this.locationData = { latitude: null, longitude: null }
      this.data = {
        name: '',
        description: '',
        address: '',
        phone: '',
        latitude: null,
        longitude: null,
        country: '',
        country_id: null
      }
    },

    onLocationChange (coordinates) {
      this.data.latitude = coordinates.latitude
      this.data.longitude = coordinates.longitude
      this.locationData = coordinates
    },

    onCoordinateChange () {
      // Update location picker when coordinates are manually entered
      if (this.data.latitude && this.data.longitude) {
        this.locationData = {
          latitude: this.data.latitude,
          longitude: this.data.longitude
        }
        if (this.$refs.locationPicker) {
          this.$refs.locationPicker.setLocation(this.data.latitude, this.data.longitude)
        }
      }
    },

    onCountryChange (countryId) {
      const selectedCountry = this.countries.find(c => c.id === countryId)
      if (selectedCountry) {
        this.data.country = selectedCountry.name

        // Reposition map to selected country if it has coordinates
        if (selectedCountry.latitude && selectedCountry.longitude) {
          this.mapCenter = {
            lat: selectedCountry.latitude,
            lng: selectedCountry.longitude
          }

          if (this.$refs.locationPicker) {
            this.$refs.locationPicker.centerMap(
              selectedCountry.latitude,
              selectedCountry.longitude,
              8
            )
          }
        }
      }
    },

    onMapError (error) {
      this.$notify({
        title: 'Location Error',
        text: error,
        type: 'warning'
      })
    },

    async loadCountries () {
      try {
        this.countries = await this.$countryService.getCountries()

        // Set default country to Cameroon if available
        const cameroon = this.countries.find(c => c.code === 'CMR' || c.name === 'Cameroon')
        if (cameroon) {
          this.data.country_id = cameroon.id
          this.data.country = cameroon.name
        }
      } catch (error) {
        console.error('Error loading countries:', error)
      }
    }
  },
  async mounted () {
    this.$emit('parent-title', this.$t('store.title.create'))
    await this.loadCountries()
  }
}
</script>
<style>
</style>
